import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {userSettingsApi} from 'api';
import {ExternalIntegrationPurposeSelect, PredefinedNotifierType} from 'components';
import {UserContext} from 'context';
import {AiProvider, ExternalIntegrationPurpose} from 'enums';
import {useApiData} from 'hooks';

import {AdvancedSettings} from './AdvancedSettings';
import {AnthropicProperties} from './AnthropicProperties';
import {Promts} from './Promts';
import {AdvancedSettingsFields, AnthropicPropertiesFields, FormFields, PromtsFields, SectionName} from './types';

export const Claude = () => {
    const {settings: {providerPurposes}} = useContext(UserContext);
    const purposes = useMemo(() => providerPurposes[AiProvider.Claude] || [], [providerPurposes]);

    const [integrationPurpose, setIntegrationPurpose] = useState<ExternalIntegrationPurpose>(ExternalIntegrationPurpose.ClientTextChat);
    const [initialValues, setInitialValues] = useState<FormFields>({
        anthropicProperties: {
            apiKey: '',
            enabled: false
        },
        advancedSettings: {
            messagesParams: {
                model: '',
            }
        },
        promts: {
            knowledgeBase: '',
            botInstruction: '',
        },
        actions: {
            callingClassificationInstruction: ''
        }
    });

    const {send: fetchSettings, data: settings, loading: fetchLoading} =
        useApiData(userSettingsApi.getClaude, {notificationType: PredefinedNotifierType.FetchError});

    const update = useCallback((section, data) =>
        userSettingsApi.updateClaude(integrationPurpose, data).then((result) => ({section, result})), [integrationPurpose]);

    const updateInitialValues = useCallback(
        ({section, result}) => setInitialValues((prev) =>
            ({...prev, [section]: result[section]})
        ),
        [setInitialValues]);

    const {send: updateSettings, loading: updateLoading} =
        useApiData(update, {
            notificationType: PredefinedNotifierType.Save,
            onSuccess: updateInitialValues
        });

    const handleSubmit = useCallback((
        section: SectionName,
        values: AnthropicPropertiesFields | AdvancedSettingsFields | PromtsFields
    ) => {
        updateSettings(section, {
            claude: {
                [section]: values
            }
        });
    }, [updateSettings]);

    const loading = useMemo(() => fetchLoading || updateLoading, [fetchLoading, updateLoading]);

    useEffect(() => {
        fetchSettings(integrationPurpose);
    }, [fetchSettings, integrationPurpose]);

    useEffect(() => {
        setInitialValues({
            anthropicProperties: settings?.anthropicProperties || {
                enabled: false,
                apiKey: ''
            },
            advancedSettings: settings?.advancedSettings || {
                messagesParams: {
                    model: '',
                }
            },
            promts: settings?.promts || {
                knowledgeBase: '',
                botInstruction: '',
            },
            actions: settings?.actions || {
                callingClassificationInstruction: ''
            }
        });
    }, [settings]);

    return (
        <Fragment>
            <ExternalIntegrationPurposeSelect
                value={integrationPurpose}
                purposes={purposes}
                className='tw-mb-4 tw-max-w-104'
                onChange={setIntegrationPurpose}
            />
            <AnthropicProperties
                initialValues={initialValues.anthropicProperties}
                loading={loading}
                onSubmit={(values) => handleSubmit(SectionName.AnthropicProperties, values)}
            />
            <Promts
                initialValues={initialValues.promts}
                loading={loading}
                onSubmit={(values) => handleSubmit(SectionName.Promts, values)}
            />
            <AdvancedSettings
                initialValues={initialValues.advancedSettings}
                loading={loading}
                onSubmit={(values) => handleSubmit(SectionName.AdvancedSettings, values)}
            />
        </Fragment>
    );
};
